<!--
Campo interno de busca (dropdown). Usado por `Suggest.vue` (listagem/PDP) e pelo
`ShortsSearchSuggestInline` na barra Shorts.
-->
<script setup lang="ts">
import type {
	SearchSuggestFieldProps,
	Suggestion,
	SuggestionCondoResult,
	SuggestionGroupKey
} from './types';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';

import {
	Command,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';
import { useNewSearchSuggest } from '~/composables/useNewSearchSuggest';
import { useSingleFieldSuggest } from '~/composables/useSingleFieldSuggest';

import HistoryGroupIcon from '~icons/material-symbols-light/history';

import CodeGroup from './CodeGroup.vue';
import { useSearchSuggestUncommittedDismiss } from './composables/useSearchSuggestUncommittedDismiss';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';
import NewSuggestInput from './NewSuggestInput.vue';
import SearchSuggestListContent from './SearchSuggestListContent.vue';
import { getSearchSuggestVariantStyles } from './variants';

const props = withDefaults(defineProps<SearchSuggestFieldProps>(), {
	isFetchingPropertyByCodeSearch: false,
	enableAnimation: true,
	closeOnClickOutside: true,
	closeOnSelect: true,
	showCloseIcon: false,
	resetSearchTermOnSelect: true,
	excludeStreetAndPoiFromLastResearchs: false,
	regionSelectTextFormat: 'default'
});

const skeletonVariant = computed(() =>
	props.variant === 'dark' ? 'primary' : 'white'
);

const variantStyles = computed(() =>
	getSearchSuggestVariantStyles(props.variant ?? 'primary')
);

const emit = defineEmits<{
	(e: 'select-address', event: SelectAddressEvent): void;
	(e: 'select-place', value: SelectAddressEvent): void;
	(e: 'select-condo', value: SuggestionCondoResult): void;
	(e: 'select-code', value: Suggestion['searchParams']): void;
	(e: 'update:search-term', value: string): void;
	(e: 'emptyResultOnFilter', searchTerm: string): void;
	(e: 'clear'): void;
	(e: 'close-modal'): void;
}>();

const isDropdownOpened = defineModel<boolean>('isDropdownOpened', {
	default: false
});
const selectedValue = ref<SuggestionGroup>();
const commandListEl = useTemplateRef<HTMLElement>('commandListEl');
const inputEl = useTemplateRef<InstanceType<typeof NewSuggestInput>>('inputEl');

function focusInput(): boolean {
	return inputEl.value?.focus() ?? false;
}

defineExpose({
	focusInput
});

const { selectedMacroregion: globalSelectedMacroregion } =
	useSingleFieldSuggest();

const {
	// State
	searchTerm,
	modelValue,
	commandEl,
	hasSelectedValue,
	isNavigatingWithKeyboard,
	backupSearchTerm,
	isUpdatingFromWatch,
	// Computed
	hasMinSearchTerm,
	searchByCodeOption,
	shouldShowCodeGroup,
	shouldShowEmptyState,
	shouldShowLastResearchs,
	locationAutocompleteData,
	loading,
	dynamicPlaceholder,

	// Research suggestions
	filteredLastResearchs,
	removeResearchs,

	// Props pass-through for styling
	placeholders,
	inputWrapperClasses,
	inputClasses,
	inputClearIconWrapperClasses,
	inputClearIconClasses,
	inputSearchIconWrapperClasses,
	inputSearchIconClasses,
	emptyStateWrapperClasses,
	emptyStateIconWrapperClasses,
	emptyStateIconClasses,
	emptyStateTitleClasses,
	emptyStateDescriptionClasses,
	groupWrapperClasses,
	groupHeadingClasses,
	groupIconClasses,
	groupItemWrapperClasses,
	groupItemTextClasses,
	groupItemSubTextClasses,

	// Functions
	clear,
	clearLocationData,
	handleKeyDown,
	getDisplayValue,
	handleModelValueUpdate: baseHandleModelValueUpdate,
	handleApplySearchTerm,
	executeLocationAutocomplete,
	setInitialSelection: setInitialSelectionFn
} = useNewSearchSuggest({
	props,
	emit,
	isDropdownOpen: isDropdownOpened,
	macroregion: globalSelectedMacroregion,
	providers: props.providers,
	bus: props.bus
});

const { applyOnCloseWithoutConfirmedSelection } =
	useSearchSuggestUncommittedDismiss({
		parentSearchTerm: () => props.searchTerm,
		onClearParentSearchTerm: () => emit('update:search-term', ''),
		searchTerm,
		modelValue,
		isUpdatingFromWatch,
		hasSelectedValue,
		backupSearchTerm,
		handleApplySearchTerm
	});

const route = useRoute();

/**
 * Lógica de placeholder baseada em rotas e contexto:
 * 1. Na página de listagem (SRP) com feature flag ativa → usa dynamicPlaceholder
 * 2. Em outras páginas (PDP dialog) com props.placeholders string → usa a string
 * 3. Caso contrário → undefined (usa o array de placeholders animados)
 */
const computedPlaceholderForInput = computed(() => {
	const isListingPage = route.name === 'PropertiesListing';

	if (isListingPage) {
		return dynamicPlaceholder.value;
	}

	if (props.inputFocusedPlaceholder) {
		return props.inputFocusedPlaceholder;
	}

	// Se props.placeholders for uma string, usa ela (caso do dialog da PDP)
	if (typeof props.placeholders === 'string') {
		return props.placeholders;
	}

	return 'Inicie sua busca';
});

const isUserFocusedOpening = ref<boolean>(false);

watch(
	() => props.searchTerm,
	value => {
		isUpdatingFromWatch.value = true;
		if (value && value.trim()) {
			handleApplySearchTerm();
		} else {
			// Trata '', undefined, null, e espaço em branco
			searchTerm.value = '';
		}
	},
	{ immediate: true }
);

function shouldSkipWatchExecution(): boolean {
	if (isUpdatingFromWatch.value) {
		isUpdatingFromWatch.value = false;
		return !isUserFocusedOpening.value;
	}
	return false;
}

function handleDropdownOpening(): void {
	const currentTerm = props.searchTerm || searchTerm.value;

	if (!currentTerm) return;

	if (isUserFocusedOpening.value) {
		backupSearchTerm.value = currentTerm;
		isUpdatingFromWatch.value = true;
		searchTerm.value = '';
	}

	isUserFocusedOpening.value = false;
}

function handleDropdownClosing(): void {
	const shouldRestoreBackup =
		backupSearchTerm.value && !searchTerm.value && !hasSelectedValue.value;

	if (shouldRestoreBackup) {
		isUpdatingFromWatch.value = true;
		searchTerm.value = backupSearchTerm.value;
	}

	backupSearchTerm.value = '';
}

function setUserFocusedOpening(value: boolean): void {
	isUserFocusedOpening.value = value;
}

function closeDropdown() {
	applyOnCloseWithoutConfirmedSelection();

	isDropdownOpened.value = false;

	if (props.closeOnSelect) {
		emit('close-modal');
	}
}

function setInitialSelection() {
	setInitialSelectionFn(selectedValue);
}

function handleInputFocus() {
	setUserFocusedOpening(true);
	openDropdown();

	if (isNavigatingWithKeyboard.value) return;

	if (!searchTerm.value || searchTerm.value.length < 3) {
		clearLocationData();
	} else {
		executeLocationAutocomplete();
	}
}

function handleModelValueUpdate(suggestion: Suggestion) {
	const shouldUpdateInternalState = props.closeOnSelect;
	baseHandleModelValueUpdate(suggestion, shouldUpdateInternalState);

	//This conditional makes the suggest not to be modified when there is a selection and the closeOnSelect prop is disabled
	//This is necessary to avoid a screen flash
	if (!shouldUpdateInternalState && suggestion.searchType !== 'code') {
		nextTick(() => {
			modelValue.value = undefined;
		});
	}

	// Limpar o searchTerm após seleção (respeita prop resetSearchTermOnSelect)
	if (props.resetSearchTermOnSelect) {
		nextTick(() => {
			searchTerm.value = '';
		});
	}

	if (props.closeOnSelect) {
		closeDropdown();
	}
}

function handleInputClear() {
	clear();
	focusInput();
	openDropdown();
	clearLocationData();
}

function handleMouseDown(event: MouseEvent) {
	if (!props.closeOnClickOutside) return;

	if (commandEl.value && !commandEl.value.contains(event.target as Node)) {
		closeDropdown();
	}
}

function handleEnter(
	_: KeyboardEvent,
	selectedElement: HTMLElement | undefined
) {
	if (shouldShowEmptyState.value) {
		clear();
		return;
	}

	selectedElement?.click();
	inputEl.value?.blur();
}

function openDropdown() {
	isDropdownOpened.value = true;
}

function handleCloseModal() {
	emit('close-modal');
}

// Seleção automática desabilitada (comportamento padrão com multi-city)

watch(
	isDropdownOpened,
	(isOpen, wasOpen) => {
		if (shouldSkipWatchExecution()) return;

		const isOpening = isOpen && !wasOpen;
		const isClosing = !isOpen && wasOpen;

		if (isOpening) {
			handleDropdownOpening();
		} else if (isClosing) {
			handleDropdownClosing();
		}
	},
	{ flush: 'sync' }
);

watch(
	() => props.isFetchingPropertyByCodeSearch,
	isFetching => {
		if (!isFetching && props.closeOnSelect) {
			closeDropdown();
		}
	}
);

watch(locationAutocompleteData, newData => {
	if (
		newData?.length &&
		!isDropdownOpened.value &&
		searchTerm.value.length >= 3 &&
		(isUserFocusedOpening.value || !props.searchTerm)
	) {
		if (!hasSelectedValue.value) {
			isDropdownOpened.value = true;
		}
	}
});

onKeyStroke('Escape', e => {
	e.preventDefault();
	if (props.closeOnClickOutside) {
		closeDropdown();
	}
});

useEventListener(document, 'mousedown', handleMouseDown);
</script>

<template>
	<div ref="commandEl">
		<Command
			v-model="modelValue"
			:open="isDropdownOpened"
			:class="cn('h-auto w-full border', props.class)"
			reset-search-term-on-blur
			:reset-search-term-on-select="props.resetSearchTermOnSelect"
			@update:model-value="value => handleModelValueUpdate(value as Suggestion)"
			@keydown="handleKeyDown"
		>
			<NewSuggestInput
				ref="inputEl"
				v-model="searchTerm"
				:placeholder="computedPlaceholderForInput"
				:focused-placeholder="props.inputFocusedPlaceholder ?? ''"
				:wrapper-classes="cn(variantStyles.inputWrapper, inputWrapperClasses)"
				:class="cn('h-[62px] text-ellipsis pl-5 text-primary', inputClasses)"
				:auto-focus="false"
				:input-clear-icon-wrapper-classes="inputClearIconWrapperClasses"
				:input-clear-icon-classes="inputClearIconClasses"
				:input-search-icon-wrapper-classes="inputSearchIconWrapperClasses"
				:input-search-icon-classes="inputSearchIconClasses"
				:enable-animation="enableAnimation"
				:show-close-icon="showCloseIcon"
				@focus.prevent="handleInputFocus"
				@clear="handleInputClear"
				@close-modal="handleCloseModal"
				@enter="handleEnter"
			>
				<template #prefix>
					<slot name="input-prefix" />
				</template>
				<template #suffix>
					<div
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="modelValue"
							:class="
								cn(
									'size-5 cursor-pointer text-white-dark-3',
									inputClearIconClasses
								)
							"
							@click="() => clear()"
						/>
					</div>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="cn('size-7 text-primary', inputSearchIconClasses)"
						/>
					</div>
				</template>
			</NewSuggestInput>

			<CommandList
				ref="commandListEl"
				:class="
					cn(
						'h-full border border-white-dark-2 border-t-0 bg-white p-0 pb-4',
						{
							'overflow-hidden': loading
						},
						listWrapperClasses
					)
				"
			>
				<SearchSuggestListContent
					:loading="loading"
					:should-show-empty-state="
						hideCodeSearch
							? !loading &&
								hasMinSearchTerm &&
								!locationAutocompleteData?.length
							: shouldShowEmptyState
					"
					:should-show-last-researchs="shouldShowLastResearchs"
					:should-show-code-group="shouldShowCodeGroup && !hideCodeSearch"
					:has-min-search-term="hasMinSearchTerm"
					:search-term="searchTerm"
					:is-fetching-property-by-code-search="isFetchingPropertyByCodeSearch"
					:skeleton-variant="skeletonVariant"
					:filtered-last-researchs="filteredLastResearchs"
					:location-autocomplete-data="locationAutocompleteData"
					:search-by-code-option="hideCodeSearch ? [] : searchByCodeOption"
					:empty-state-wrapper-classes="emptyStateWrapperClasses"
					:empty-state-icon-wrapper-classes="emptyStateIconWrapperClasses"
					:empty-state-icon-classes="emptyStateIconClasses"
					:empty-state-title-classes="emptyStateTitleClasses"
					:empty-state-description-classes="emptyStateDescriptionClasses"
					:separator-classes="variantStyles.separator"
					:group-wrapper-classes="groupWrapperClasses"
					:group-heading-classes="groupHeadingClasses"
					:group-icon-classes="groupIconClasses"
					:group-item-wrapper-classes="groupItemWrapperClasses"
					:group-item-text-classes="groupItemTextClasses"
					:group-item-sub-text-classes="groupItemSubTextClasses"
					:variant-styles="variantStyles"
					@remove-suggestion="removeResearchs"
				/>
			</CommandList>
		</Command>
	</div>
</template>
